// Hooks for retreiving schemas from left/right node
import { useMemo } from 'react'
import { useStore } from '@xyflow/react'

import {
  Node as TNode,
  NodeSchema,
  ClearNode,
  DateParserNode,
  InputNode,
  MapNode,
  LowerNode,
  LeftJoinNode,
  ConcatenateNode,
  UniteNode,
  AggregateNode,
  InputColumn,
  TrimNode,
  CastNode,
  CalculateNode,
  FilterNode,
  ReplaceNode,
  TextReplaceNode,
  TimeDifferenceNode
} from 'types/transformation/types/nodes'
import NodeType from 'types/transformation/enums/NodeType'
import { SourceTable } from 'types/GlobalDataset'

import { useSourceSchemas } from 'redux/hooks/SimTables'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { getColumnName } from '../utils'

function getNodeById(
  nodes: TNode['data'][],
  nodeId: string
): TNode['data'] | undefined {
  return nodes.find((node) => node.id === nodeId)
}

function formatSourceSchema(
  node: TNode['data'],
  table: SourceTable
): InputColumn[] {
  return table.columns.map((column) => ({
    id: getColumnName(node.id, column.technical_name),
    name: column.label,
    type: column.type,
    reference_node: node.id,
    reference_node_index: node.index,
    source_column_id: column.value
  }))
}

function getSchemaForNode(
  nodes: TNode['data'][],
  nodeId: string,
  sourceTables: Map<string, SourceTable>
): NodeSchema[] {
  const node = getNodeById(nodes, nodeId)

  if (!node) throw new Error(`Node with id ${nodeId} not found`)

  switch (node.type) {
  case NodeType.AGGREGATE: {
    const { group_by, left_node, schema } = node as AggregateNode

    const leftNodeSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []

    // get group by column from left node
    const groupByColumn = group_by
      ? leftNodeSchema.find((item) => item.id === group_by)
      : null

    const outputSchema: NodeSchema[] = [...schema]

    if (groupByColumn) {
      // add group by column but update node ownership
      outputSchema.push({
        ...groupByColumn,
        reference_node: node.id,
        reference_node_index: node.index
      })
    }

    return outputSchema
  }
  case NodeType.CALCULATE: {
    const { left_node, new_column } = node as CalculateNode

    const leftNodeSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []

    return [...leftNodeSchema, new_column]
  }
  case NodeType.CAST: {
    const { left_node, change_column } = node as CastNode

    const leftNodeSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []

    if (!change_column) {
      return leftNodeSchema
    }

    // replace changed column
    return leftNodeSchema.map((col) =>
      col.id === change_column.id ? change_column : col
    )
  }
  case NodeType.CLEAR: {
    const { left_node, columns } = node as ClearNode

    const leftNodeSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []

    // remove selected columns
    return leftNodeSchema.filter((col) => !columns.includes(col.id))
  }
  case NodeType.FILTER: {
    const { left_node } = node as FilterNode

    return left_node ? getSchemaForNode(nodes, left_node, sourceTables) : []
  }
  case NodeType.INPUT: {
    const sourceTableId = (node as InputNode).source_table_id
    const sourceTable = sourceTableId ? sourceTables.get(sourceTableId) : null

    return sourceTable ? formatSourceSchema(node, sourceTable) : []
  }
  case NodeType.MAP: {
    const { schema } = node as MapNode

    return schema
  }
  case NodeType.LOWER: {
    const { left_node } = node as LowerNode

    return left_node ? getSchemaForNode(nodes, left_node, sourceTables) : []
  }
  case NodeType.LEFT_JOIN: {
    const { left_node, right_node } = node as LeftJoinNode

    const leftSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []
    const rightSchema = right_node
      ? getSchemaForNode(nodes, right_node, sourceTables)
      : []

    return [...leftSchema, ...rightSchema]
  }
  case NodeType.CONCATENATE: {
    const { left_node, columns } = node as ConcatenateNode

    const leftSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []

    return [...leftSchema, ...columns]
  }
  case NodeType.DATE_PARSER: {
    const { left_node, new_column } = node as DateParserNode

    const leftNodeSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []

    // Always adds new column with parsed date
    return [...leftNodeSchema, new_column]
  }
  case NodeType.REPLACE: {
    const { left_node, new_column } = node as ReplaceNode

    const leftNodeSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []

    // If creating new column, add it. Otherwise, schema stays the same
    if (new_column) {
      return [...leftNodeSchema, new_column]
    }

    return leftNodeSchema
  }
  case NodeType.TIME_DIFFERENCE: {
    const { left_node, new_column } = node as TimeDifferenceNode

    const leftNodeSchema = left_node
      ? getSchemaForNode(nodes, left_node, sourceTables)
      : []

    // Always adds new column with time difference result
    return [...leftNodeSchema, new_column]
  }
  case NodeType.TRIM: {
    const { left_node } = node as TrimNode

    return left_node ? getSchemaForNode(nodes, left_node, sourceTables) : []
  }
  case NodeType.TEXT_REPLACE: {
    const { left_node } = node as TextReplaceNode

    return left_node ? getSchemaForNode(nodes, left_node, sourceTables) : []
  }
  case NodeType.UNITE: {
    const { schema } = node as UniteNode

    return schema
  }
  // TODO: more nodes

  default:
    throw new Error(`Unsupported node type: ${node.type}`)
  }
}

// single child node
export function useInputSchema(nodeId: string | null): NodeSchema[] {
  const [sourceTableMap] = useSourceSchemas()

  // Subscribe only to upstream nodes relevant for this nodeId
  const relevantNodes = useStore(
    (s) => {
      if (!nodeId) return [] as TNode['data'][]

      const { nodes } = s
      const visited = new Set<string>()
      const stack: string[] = [nodeId]
      const result: TNode['data'][] = []
      const nodeMap = new Map(nodes.map((node) => [node.id, node]))

      while (stack.length) {
        const id = stack.pop() as string

        if (!id || visited.has(id)) continue
        visited.add(id)

        const n = nodeMap.get(id)

        if (!n) continue

        const data = n.data as TNode['data']

        result.push(data)

        const left = (data as any).left_node as string | null | undefined
        const right = (data as any).right_node as string | null | undefined

        if (left) stack.push(left)
        if (right) stack.push(right)
      }

      return result
    },
    // shallow ref equality on array entries
    (a, b) =>
      a === b || (a.length === b.length && a.every((v, i) => v === b[i]))
  )

  return useMemo(() => {
    if (!nodeId) return []
    if (relevantNodes.length === 0) return []
    const schema = getSchemaForNode(relevantNodes, nodeId, sourceTableMap)

    return schema.slice().sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
  }, [nodeId, relevantNodes, sourceTableMap])
}

// double child node
export function useInputSchemas(ids: {
  left: string | null
  right: string | null
}): { left: NodeSchema[]; right: NodeSchema[] } {
  return {
    left: useInputSchema(ids.left),
    right: useInputSchema(ids.right)
  }
}
